import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBestaetigung } from '@pilot-hub/ui'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, useNeuParam, FilterZurueck, KennzahlenLeiste } from '../components/ListenWerkzeuge'
import { FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge, ChipListe } from '../components/StatusBadge'
import { PageHeader, Card, Button, Badge, Input, Select, Edit, Eye, Trash2 } from '@pilot-hub/ui'
import { userApi, api } from '@pilot-hub/api-client'
import { rollenLabel, funktionenLabel } from '@pilot-hub/config'
import { FileText, ArrowLeft, Plus, Save } from 'lucide-react'

interface Nutzer {
  id: number
  benutzername: string
  email: string
  vorname: string | null
  nachname: string | null
  telefon: string | null
  aktiv: boolean
  letzter_login: string | null
  erstellt_am: string | null
  rollen: string[]
  funktionen: string[]
}

interface Auswahl { id: number; name: string; beschreibung?: string | null }

const LEER = {
  benutzername: '', vorname: '', nachname: '', email: '', telefon: '', password: '', aktiv: 'true'
}

const fmtDT = (s: string | null) =>
  s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; nutzerId?: number }

export function UsersPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const navigate = useNavigate()
  const [nutzer, setNutzer] = useState<Nutzer[]>([])
  const [rollen, setRollen] = useState<Auswahl[]>([])
  const [funktionen, setFunktionen] = useState<Auswahl[]>([])
  const [search, setSearch] = usePersistState('nutzer.search', '')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [aktivFilter, setAktivFilter] = usePersistState('nutzer.aktivFilter', 'all')
  const [rollenFilter, setRollenFilter] = usePersistState<string>('nutzer.rollenFilter', 'all')
  const [funktionenFilter, setFunktionenFilter] = usePersistState<string>('nutzer.funktionenFilter', 'all')

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [gewaehlteRollen, setGewaehlteRollen] = useState<string[]>([])
  const [gewaehlteFunktionen, setGewaehlteFunktionen] = useState<string[]>([])
  const [speichert, setSpeichert] = useState(false)

  const geaendert = useUngespeichert(form, JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [n, r, f] = await Promise.all([
        userApi.list({ size: 500 }),
        api.get('/rollen').catch(() => ({ data: [] })),
        api.get('/funktionen').catch(() => ({ data: [] }))
      ])
      setNutzer((n.data as any).items ?? [])
      setRollen(Array.isArray(r.data) ? (r.data as Auswahl[]) : [])
      setFunktionen(Array.isArray(f.data) ? (f.data as Auswahl[]) : [])
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Benutzer konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])
  useNeuParam(oeffneNeu, !loading)

  function toggle(liste: string[], setter: (v: string[]) => void, wert: string) {
    setter(liste.includes(wert) ? liste.filter(x => x !== wert) : [...liste, wert])
  }

  function oeffneNeu() {
    setForm({ ...LEER })
    setGewaehlteRollen([])
    setGewaehlteFunktionen([])
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(u: Nutzer) {
    setForm({
      benutzername: u.benutzername ?? '',
      vorname: u.vorname ?? '',
      nachname: u.nachname ?? '',
      email: u.email ?? '',
      telefon: u.telefon ?? '',
      aktiv: u.aktiv ? 'true' : 'false'
    })
    setGewaehlteRollen(u.rollen ?? [])
    setGewaehlteFunktionen(u.funktionen ?? [])
    setView({ mode: 'edit', nutzerId: u.id })
  }

  function oeffneDetail(u: Nutzer) {
    setView({ mode: 'detail', nutzerId: u.id })
    setForm({
      benutzername: u.benutzername,
      email: u.email,
      vorname: u.vorname || '',
      nachname: u.nachname || '',
      telefon: u.telefon || '',
      aktiv: String(u.aktiv),
      password: '',
    })
    setGewaehlteRollen(u.rollen || [])
    setGewaehlteFunktionen(u.funktionen || [])
  }

  function zurueckZurListe() {
    setView({ mode: 'list' })
    setForm({ ...LEER })
    setGewaehlteRollen([])
    setGewaehlteFunktionen([])
  }

  async function anlegen() {
    setFehler(null)
    if (!form.benutzername.trim() || !form.email.trim()) {
      setFehler('Benutzername und E-Mail sind erforderlich.')
      return
    }
    if (form.password.length < 8) {
      setFehler('Das Passwort muss mindestens 8 Zeichen lang sein.')
      return
    }
    setSpeichert(true)
    try {
      await userApi.create({
        benutzername: form.benutzername.trim(),
        email: form.email.trim(),
        password: form.password,
        vorname: form.vorname || null,
        nachname: form.nachname || null,
        telefon: form.telefon || null,
        aktiv: form.aktiv === 'true',
        rollen: gewaehlteRollen,
        funktionen: gewaehlteFunktionen
      })
      zurueckZurListe()
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Anlegen fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  async function speichern() {
    if (!view.nutzerId) return
    setFehler(null)
    if (form.password && form.password.length < 8) {
      setFehler('Das neue Passwort muss mindestens 8 Zeichen lang sein.')
      return
    }
    setSpeichert(true)
    try {
      await userApi.update(view.nutzerId, {
        benutzername: form.benutzername,
        vorname: form.vorname || null,
        nachname: form.nachname || null,
        email: form.email,
        telefon: form.telefon || null,
        aktiv: form.aktiv === 'true',
        rollen: gewaehlteRollen,
        funktionen: gewaehlteFunktionen,
        // nur mitschicken, wenn ein neues Passwort eingegeben wurde
        ...(form.password ? { password: form.password } : {}),
      })
      zurueckZurListe()
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  const gefiltert = nutzer.filter(u => {
    const matchesSearch = (u.benutzername ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.vorname ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.nachname ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.funktionen ?? []).join(' ').toLowerCase().includes(search.toLowerCase()) ||
      (u.rollen ?? []).join(' ').toLowerCase().includes(search.toLowerCase())

    const matchesRolle = rollenFilter === 'all' || (u.rollen ?? []).includes(rollenFilter)
    const matchesFunktion = funktionenFilter === 'all' || (u.funktionen ?? []).includes(funktionenFilter)

    const matchesAktiv = aktivFilter === 'all' || (aktivFilter === 'aktiv') === !!u.aktiv
    return matchesSearch && matchesRolle && matchesFunktion && matchesAktiv
  })

  const columns: SpalteDef[] = [
    { key: 'benutzername', header: 'Benutzername', accessor: 'benutzername', render: (_v: any, u: any) => <span className="font-medium">{u.benutzername}</span> },
    { key: 'vorname', header: 'Vorname', accessor: 'vorname', render: (_v: any, u: any) => u.vorname || '—' },
    { key: 'nachname', header: 'Nachname', accessor: 'nachname', render: (_v: any, u: any) => u.nachname || '—' },
    { key: 'funktionen', header: 'Rolle / Funktion', accessor: 'funktionen', sortWert: (u: any) => (u.rollen ?? []).map(rollenLabel).join(', ') || null, render: (_v: any, u: any) => {
      const rollen: string[] = u.rollen || []
      const funktionen: string[] = u.funktionen || []
      if (rollen.length === 0 && funktionen.length === 0) return <span className="text-xs text-dark-text-subtle">—</span>
      return (
        <div className="flex flex-col gap-1">
          {rollen.length > 0 && <ChipListe items={rollen.map(rollenLabel)} klasse="border-emerald-500/30 bg-emerald-500/10 text-emerald-200" />}
          {funktionen.length > 0 && <ChipListe items={funktionen.map(funktionenLabel)} />}
        </div>
      )
    }},
    { key: 'aktiv', header: 'Status', accessor: 'aktiv', sortWert: (u: any) => u.aktiv ? 0 : 1, render: (_v: any, u: any) => <StatusBadge wert={u.aktiv ? 'AKTIV' : 'INAKTIV'} /> },
    { key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, row: any) => (
      <ZeilenAktionen name={String(row.benutzername)} onBearbeiten={() => oeffneBearbeiten(row)} eintraege={(row.funktionen ?? []).some((f: string) => f === 'PILOT' || f === 'CO_PILOT') ? [{ label: 'Flugbuch exportieren', icon: <FileText className="h-4 w-4" />, onClick: () => navigate(`/export?art=pilot&id=${row.id}`) }] : undefined} onLoeschen={async () => { const name = row.benutzername; if (await bestaetigen({ titel: 'Benutzer löschen?', text: 'Benutzer', objekt: String(name), hinweis: 'Rollen, Funktionen, Lizenzen und Einsatz-Teilnahmen dieses Benutzers werden entfernt. Flüge und Einsätze bleiben erhalten, verlieren aber den Bezug zu ihm.' })) userApi.delete(row.id).then(laden) }} />
    )},
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          subtitle="Alle Benutzer der ASB Drohnenstaffel — Rollen und Funktionen verwalten"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neuer Benutzer</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <Card>
          <div className="mb-4 flex flex-col sm:flex-row gap-4 items-center sm:items-center sm:justify-start">
            <Input placeholder="Benutzername, Name" value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-80" />
            <div className="grid gap-4 sm:grid-cols-[minmax(9rem,auto)_minmax(12rem,auto)] sm:shrink-0 w-full sm:w-auto">
              <Select value={rollenFilter} onChange={e => setRollenFilter(e.target.value)}
                options={[{ value: 'all', label: 'Alle Rollen' }, ...rollen.map(r => ({ value: r.name, label: rollenLabel(r.name) }))]}
                placeholder="— Rolle filtern —" />
              <Select value={funktionenFilter} onChange={e => setFunktionenFilter(e.target.value)}
                options={[{ value: 'all', label: 'Alle Funktionen' }, ...funktionen.map(f => ({ value: f.name, label: funktionenLabel(f.name) }))]}
                placeholder="— Funktion filtern —" />
            </div>
            <FilterZurueck aktiv={!!search || rollenFilter !== 'all' || funktionenFilter !== 'all' || aktivFilter !== 'all'} onClick={() => { setSearch(''); setRollenFilter('all'); setFunktionenFilter('all'); setAktivFilter('all') }} />
            <div className="text-sm text-dark-text-muted whitespace-nowrap sm:ml-auto">{gefiltert.length} von {nutzer.length}</div>
          </div>
          <div className="mb-4"><KennzahlenLeiste items={[
            { label: 'Alle Benutzer', anzahl: nutzer.length, aktiv: aktivFilter === 'all', onClick: () => setAktivFilter('all') },
            { label: 'Aktiv', anzahl: nutzer.filter(u => u.aktiv).length, aktiv: aktivFilter === 'aktiv', punkt: 'bg-emerald-400', onClick: () => setAktivFilter(aktivFilter === 'aktiv' ? 'all' : 'aktiv') },
            { label: 'Inaktiv', anzahl: nutzer.filter(u => !u.aktiv).length, aktiv: aktivFilter === 'inaktiv', punkt: 'bg-slate-400', onClick: () => setAktivFilter(aktivFilter === 'inaktiv' ? 'all' : 'inaktiv') },
          ]} /></div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Benutzer…</div> : (
            <ListenTabelle persistKey="nutzer" data={gefiltert} columns={columns} keyAccessor={(u: any) => String(u.id)} onRowClick={oeffneDetail} emptyMessage="Keine Benutzer gefunden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW ======
  const currentNutzer = view.nutzerId ? nutzer.find(u => u.id === view.nutzerId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCreate ? 'Neuer Benutzer' : isEdit ? 'Benutzer bearbeiten' : 'Benutzer anzeigen'}
        subtitle={isCreate ? 'Neuen Benutzer anlegen' : currentNutzer ? `@${currentNutzer.benutzername}` : ''}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentNutzer && <Button onClick={() => oeffneBearbeiten(currentNutzer)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          </div>
        }
      />

      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

      <Card>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Benutzername *</label>
              <Input value={form.benutzername} onChange={e => setForm({...form, benutzername: e.target.value})} disabled={isDetail} placeholder="z.B. max.muster" />
            </div>
            <div>
              <label className="label">E-Mail *</label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} disabled={isDetail} placeholder="max@asb.de" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">{isCreate ? 'Passwort *' : 'Neues Passwort'}</label>
              <Input type="password" autoComplete="new-password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} disabled={isDetail}
                placeholder={isCreate ? 'Min. 8 Zeichen' : 'Leer lassen = unverändert'} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Vorname</label>
              <Input value={form.vorname} onChange={e => setForm({...form, vorname: e.target.value})} disabled={isDetail} placeholder="Max" />
            </div>
            <div>
              <label className="label">Nachname</label>
              <Input value={form.nachname} onChange={e => setForm({...form, nachname: e.target.value})} disabled={isDetail} placeholder="Muster" />
            </div>
          </div>
          <div>
            <label className="label">Telefon</label>
            <Input value={form.telefon} onChange={e => setForm({...form, telefon: e.target.value})} disabled={isDetail} placeholder="+49 151 1234567" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Status</label>
              <Select value={form.aktiv} onChange={e => setForm({...form, aktiv: e.target.value})} disabled={isDetail}
                options={[{ value: 'true', label: 'Aktiv' }, { value: 'false', label: 'Inaktiv' }]} />
            </div>
          </div>

          {(!isDetail) && (
            <>
              <div>
                <label className="label">Rollen</label>
                <div className="flex flex-wrap gap-2">
                  {rollen.map(r => (
                    <label key={r.name} className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer hover:bg-dark-bg-tertiary">
                      <input type="checkbox" checked={gewaehlteRollen.includes(r.name)} onChange={e => toggle(gewaehlteRollen, setGewaehlteRollen, r.name)} className="rounded border-dark-border" />
                      <span>{rollenLabel(r.name)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Funktionen</label>
                <div className="flex flex-wrap gap-2">
                  {funktionen.map(f => (
                    <label key={f.name} className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer hover:bg-dark-bg-tertiary">
                      <input type="checkbox" checked={gewaehlteFunktionen.includes(f.name)} onChange={e => toggle(gewaehlteFunktionen, setGewaehlteFunktionen, f.name)} className="rounded border-dark-border" />
                      <span>{funktionenLabel(f.name)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {isDetail && currentNutzer && (
            <>
              <div className="pt-4 border-t border-dark-border">
                <h3 className="mb-3 font-medium text-dark-text">Rollen</h3>
                <div className="flex flex-wrap gap-2">
                  {currentNutzer.rollen.map(r => <Badge key={r} variant="success">{rollenLabel(r)}</Badge>)}
                </div>
              </div>
              <div className="pt-4 border-t border-dark-border">
                <h3 className="mb-3 font-medium text-dark-text">Funktionen</h3>
                <div className="flex flex-wrap gap-2">
                  {currentNutzer.funktionen.map(f => <Badge key={f} variant="info">{funktionenLabel(f)}</Badge>)}
                </div>
              </div>
              <div className="pt-4 border-t border-dark-border">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-dark-text-muted">Letzter Login:</span> <span>{fmtDT(currentNutzer.letzter_login)}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Erstellt am:</span> <span>{fmtDT(currentNutzer.erstellt_am)}</span></div>
                </dl>
              </div>
            </>
          )}
        </div>
      </Card>
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={false} onSpeichern={isCreate ? anlegen : speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}